import { useState } from 'react';
import { Card, Collapse, Button, DatePicker, Progress, Radio, Typography, Space, Tag, Alert, Tooltip } from 'antd';
import { BankOutlined, CheckOutlined, ExclamationOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Document, PDFDownloadLink } from '@react-pdf/renderer';
import { reduce as _reduce } from 'lodash';
import moment from 'moment';
import {
  convertCentsIntoDollar,
  formatDate,
  isSupportAgent2,
  isSupportAgent3,
  findFurnishingAccount,
  toWriteOff,
} from '../../../../helpers/genericHelper';
import { MyDocument } from '../PdfFile';
import SBPaymentHistoryComponent from '../SBPaymentHistory';
import Data from '../../../CustomText/data';
import StatementList from './StatementsList';
import PaymentSchedule from './PaymentSchedule';
import PaymentAttemptsComponent from '../PaymentAttempts';
import PaymentAttemptsList from './PaymentAttemptsList';
import { PersistedCollapse } from '../../../PersistedSections';
import usePageState from '../../../../helpers/usePageState';
import ActionsMenu from '../../../ActionsMenu';

const { Panel } = Collapse;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const CountTag = ({ count, label, problem = false }) => {
  if (count == null) return null;
  return <Tag color={problem && count > 0 ? "error" : "default"}>{label ? `${count} ${label}` : count}</Tag>;
};

const INSTALLMENT_LABELS = {
  paid: 'Paid',
  late: 'Paid late',
  pending: 'In progress',
  overdue: 'Overdue',
  upcoming: 'Upcoming'
};

const InstallmentDot = ({ installment }) => {
  const { state, number, date, amount } = installment;
  const icon = {
    paid: <CheckOutlined />,
    late: <CheckOutlined />,
    pending: <ClockCircleOutlined />,
    overdue: <ExclamationOutlined />
  }[state];

  return (
    <Tooltip title={`#${number} · ${INSTALLMENT_LABELS[state]}${date ? ` · due ${formatDate(date)}` : ''}${amount ? ` · ${convertCentsIntoDollar(amount)}` : ''}`}>
      <div className="summary-installment">
        <div className={`summary-installment-dot ${state}`}>{icon}</div>
        <small>{date ? moment(date).format('MMM') : `#${number}`}</small>
      </div>
    </Tooltip>
  );
};

export default function LoanDetails({
  data,
  loanData,
  userSummary,
  showRefundModalVisible,
  showCloseAccountModal,
  openToggleAutoRenewModal,
  showChargeSilaModal,
  paymentAttempts,
  autoPayStatusEnabled,
  autoPayStatusData,
  autoPayAction,
  furnishingAccounts
}) {

    const [startDate, setStartDate] = useState(moment().subtract(6, 'months'));
    const [endDate, setEndDate] = useState(moment());
    const [paymentType, setPaymentType] = usePageState(`loan-payment-type:${data?.sbLoanEntry?.id}`, 'payments');

    if (!loanData || !data) {
      return <>Loan details are not available</>;
    }

    const { loanDetails, loanStatements } = loanData[data.sbLoanEntry?.id] || false;
    if (!data || !data.sbLoanEntry) { return <>Cannot Load Loan</>; }


    const handleDateChange = (dates) => {
      setStartDate(dates[0]);
      setEndDate(dates[1])
    }

    const sbPayments = _reduce(data?.allPayments, (accumulator, payment) => {
      accumulator.push({
          //...payment,
          debit: true,
          amountInCents: payment?.amount || 0,
          transactionTime: payment?.paymentDueDate || 0,
          descriptor: 'Payment Due'
      });

      if (payment?.paymentSuccessTs) {
          accumulator.push({
          //...payment,
          debit: false,
          amountInCents: payment?.amount || 0,
          transactionTime: payment?.paymentSuccessTs || 0,
          descriptor: 'Payment Received'
          });
      }
      return accumulator;
    }, []);

    if (data?.sbLoanEntry?.loanStatus === 'CANCELLED') {
      sbPayments.push({
          debit: false,
          amountInCents: data?.remainingDeposits || 0,
          transactionTime: data?.closureDate || 0,
          descriptor: 'Payment Received'
      });
    }

    const totalCost = data.loanMetadata?.costInCents || 0;
    const monthlyCost = totalCost / 12;
    const principalMonthly = data.monthlyPlanAmount - monthlyCost;

    const paymentAmount = loanStatements.toSorted(
      (a,b) => new Date(b.statementData.statementDate) - new Date(a.statementData.statementDate)
    )?.[0]?.statementData?.currentBalance
      || data?.monthlyPlanAmount || 0;
    const autopayMinBalance = Math.max(10000, Math.round(paymentAmount * 1.5));
    const manualPaymentMinBalance = Math.max(5000, Math.round(paymentAmount * 1.5));

    const pillColor = (value) => {
      switch (value) {
        case 'IN_PROGRESS':
        case 'ACTIVE':
          return 'processing';
        case 'PENDING':
          return 'warning';
        case 'DELINQUENT':
          return 'error';
        case 'COMPLETED':
          return 'success';
        case 'CHARGED_OFF':
        case 'CANCELLED':
        case 'CLOSED':
          return 'error';
        default:
          return 'default';
      }
    }

    // Same rules as the old progress bar colors: paid after the due date counts as late,
    // and an unpaid installment past its due date is overdue
    const getInstallmentState = (paymentStatus, dueDate, successTime) => {
      const isAfterDue = (time) => dueDate && time && moment(time).isAfter(moment(`${dueDate}T23:59:59Z`));
      switch (paymentStatus) {
        case 'SUCCESS':
          return isAfterDue(successTime) ? 'late' : 'paid';
        case 'IN_PROGRESS':
          return 'pending';
        default:
          if (dueDate && (!successTime || isAfterDue(successTime)) && moment().isAfter(moment(`${dueDate}T23:59:59Z`))) {
            return 'overdue';
          }
          return 'pending';
      }
    }

    const uniquePayments = (payments) => {
      const unique = {};
      return (payments || []).toSorted((a, b) => a.paymentNumber - b.paymentNumber).filter(payment => {
        if (!unique[payment.paymentNumber]) {
          unique[payment.paymentNumber] = true;
          return true;
        }
        return false;
      });
    }

    const payments = uniquePayments(data.allPayments);
    const loanTerm = loanDetails?.type?.term || 0;
    const remainingMonths = data?.remainingMonths || 0;
    const amortizations = loanDetails?.amortizations || [];

    // One entry per installment of the plan: the payment if there is one, otherwise the scheduled date
    const installments = Array.from({ length: Math.max(loanTerm, payments.length) }, (_, index) => {
      const number = index + 1;
      const payment = payments.find((p) => p.paymentNumber === number);
      const scheduled = amortizations.find((a) => a.paymentNumber === number);
      return {
        number,
        state: payment ? getInstallmentState(payment.paymentStatus, payment.paymentDueDate, payment.paymentSuccessTs) : 'upcoming',
        date: payment?.paymentDueDate || scheduled?.scheduledDate,
        amount: payment?.amount || scheduled?.paymentAmount
      };
    });
    const paidCount = installments.filter((i) => i.state === 'paid' || i.state === 'late').length;
    const overdueCount = installments.filter((i) => i.state === 'overdue').length;
    // What is overdue: the missed installments added up, and the earliest missed due date
    const overdueInstallments = installments.filter((i) => i.state === 'overdue');
    const overdueCents = overdueInstallments.reduce((sum, i) => sum + (i.amount || loanDetails?.type?.monthlyPayment || 0), 0);
    const oldestOverdueDate = overdueInstallments.map((i) => i.date).filter(Boolean).sort()[0];
    const isPlanDelinquent = loanDetails?.loanStatus === 'DELINQUENT' || overdueCount > 0;
    const nextInstallment = installments.find((i) => i.state === 'upcoming' || i.state === 'pending');

    const planAmount = loanDetails?.type?.amount || 0;
    const savedCents = data?.successfulPaymentsTotal || 0;
    const savedPercent = planAmount > 0 ? Math.min(100, Math.round(savedCents / planAmount * 100)) : 0;
    const isClosed = !!data?.closureDate;
    // The tradeline this plan is reported to the bureaus under
    const tradeline = findFurnishingAccount(
      furnishingAccounts,
      [data?.sbLoanEntry?.id, data?.id, loanDetails?.id, data?.sbLoanEntry?.loanId, loanDetails?.loanId,
        data?.loanMetadata?.id, data?.loanMetadata?.loanId, data?.sbLoanEntry?.savingsBuilderId]
    );

    const alerts = [
      loanDetails?.loanChargeoffTs && { type: 'error', message: `Plan written off on ${formatDate(loanDetails.loanChargeoffTs)}.` },
      loanDetails?.loanClosureTs && { type: 'warning', message: `Plan closed on ${formatDate(loanDetails.loanClosureTs)}.` },
      overdueCount > 0 && {
        type: 'error',
        message: `${overdueCount} overdue ${overdueCount === 1 ? 'installment' : 'installments'}. See Payment History for attempts.`
      },
      !autoPayStatusEnabled && !isClosed && { type: 'warning', message: 'AutoPay is off for this plan.' },
      overdueCount === 0 && !isClosed && nextInstallment?.date && {
        type: 'success',
        message: `No overdue installments. Next installment${nextInstallment.amount ? ` ${convertCentsIntoDollar(nextInstallment.amount)}` : ''} on ${formatDate(nextInstallment.date)}.`
      }
    ].filter(Boolean);

    const actionGroups = [
      {
        title: 'Payments',
        items: [
          isSupportAgent3() && !isClosed && <Button key="retry" onClick={() => showChargeSilaModal(data)}>Retry Payment</Button>,
          isSupportAgent2() && <Button key="refund" onClick={() => showRefundModalVisible(data)}>Refund</Button>,
          autoPayAction && <span key="autopay">{autoPayAction}</span>
        ]
      },
      {
        title: 'Plan',
        items: [
          isSupportAgent2() && !isClosed && <Button key="auto-renew" onClick={() => openToggleAutoRenewModal(data)}>Toggle Auto Renew</Button>
        ]
      },
      {
        title: 'Danger Zone',
        danger: true,
        items: [
          isSupportAgent2() && !isClosed && <Button key="close" danger onClick={() => showCloseAccountModal(data)}>Close Account</Button>
        ]
      }
    ];

    const downloadStatement = userSummary && (
      <Space wrap size={8} onClick={(e) => e.stopPropagation()}>
        <RangePicker size="small" defaultValue={[startDate, endDate]} onChange={handleDateChange} />
        <PDFDownloadLink
            document={
            <Document>
                <MyDocument
                title="Ava Save & Build Credit Statement"
                summaryData={userSummary}
                startDate={startDate}
                endDate={endDate}
                last4={data.lastFour}
                balanceDue={convertCentsIntoDollar((data?.sbLoanEntry?.loanStatus === 'CANCELLED' ? 0 : data?.remainingDeposits) || 0)}
                list={sbPayments || []}
                financialProduct={loanDetails.fundsPlatform === "GALILEO"}
                subtext={data.hasOriginationFee ? `Principal: ${convertCentsIntoDollar(principalMonthly)}, Fees: ${convertCentsIntoDollar(monthlyCost)}` : false}/>
            </Document>
            }
            fileName={`Ava Statement, ${moment(startDate).format('MMM-DD-YYYY')} - ${moment(endDate).format('MMM-DD-YYYY')}`}
        >
            {({ documentLoading }) => <Button size="small">{documentLoading ? 'Downloading Statement...' : 'Download Statement'}</Button>}
        </PDFDownloadLink>
      </Space>
    );

    return (
    <div className="summary-page mb-24" key={data.id}>
      {/* Header */}
      <Card size="small" className="summary-header">
        <div className="summary-header-row">
          <div className="summary-avatar"><BankOutlined /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={8} wrap>
              <Text strong style={{ fontSize: 18 }}>Save &amp; Build Credit</Text>
              <Tag color={pillColor(loanDetails.loanStatus)}>{toWriteOff(loanDetails.loanStatus)}</Tag>
              {loanTerm > 0 && <Tag>{loanTerm}-month plan</Tag>}
            </Space>
            <div className="card-header-info">
              <Space size={6} wrap split={<Text type="secondary">·</Text>}>
                <Text>Originated {loanDetails.loanOrigination ? formatDate(loanDetails.loanOrigination) : 'N/A'}</Text>
                <Text>
                  Tradeline Number:{' '}
                  {tradeline?.accountNumber
                    ? <Text strong copyable>{tradeline.accountNumber}</Text>
                    : <Text type="secondary">—</Text>}
                </Text>
                <Text>Origination fee: {data.hasOriginationFee ? 'yes' : 'no'}</Text>
                <Text>Statements {loanDetails.billingStatementDay ? 'on' : 'off'}</Text>
              </Space>
            </div>
            <div className="card-header-info">
              <Space size={6} wrap split={<Text type="secondary">·</Text>}>
                <Text>Billing day {loanDetails?.billingStatementDay ?? 'N/A'}</Text>
                <Text>Due day {loanDetails?.paymentDueDay ?? 'N/A'}{loanDetails?.dueDaySetByUser ? ' (set by user)' : ''}</Text>
                <Text>
                  AutoPay {autoPayStatusEnabled ? 'on' : 'off'}{autoPayStatusData?.autoPayDay ? `, day ${autoPayStatusData.autoPayDay}` : ''}
                </Text>
                <Text>Auto renew {loanDetails?.autoRenews ? 'on' : 'off'}</Text>
              </Space>
            </div>
          </div>
          <ActionsMenu groups={actionGroups} />
        </div>
      </Card>

      {alerts.length > 0 &&
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {alerts.map((alert) => <Alert key={alert.message} showIcon type={alert.type} message={alert.message} />)}
        </Space>
      }

      {/* Plan progress */}
      <Card size="small" title="Plan Progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
          <Data title="Total $ in S&B Credit Wallet (DB)">
            <Text strong style={{ fontSize: 22 }}>{convertCentsIntoDollar(savedCents)}</Text>
            <Text type="secondary"> of {convertCentsIntoDollar(planAmount)} plan</Text>
          </Data>
          <div style={{ textAlign: 'right' }}>
            <Data title="Remaining deposits to complete plan">
              <Text strong>{convertCentsIntoDollar(planAmount - (data?.savingBuilderWalletTotal || 0))}</Text>
              <Text type="secondary"> · {remainingMonths} of {loanTerm} installments left</Text>
            </Data>
          </div>
        </div>
        <Progress percent={savedPercent} showInfo={false} strokeColor="#a448ff" />

        {installments.length > 0 && <>
          <Text type="secondary" strong><small>INSTALLMENTS · {paidCount} of {installments.length} paid</small></Text>
          <div className="summary-installments">
            {installments.map((installment) => <InstallmentDot key={installment.number} installment={installment} />)}
          </div>
          <Space size={12} wrap>
            {['paid', 'late', 'pending', 'overdue', 'upcoming'].map((state) => (
              <Text type="secondary" key={state}><small><span className={`summary-installment-key ${state}`} /> {INSTALLMENT_LABELS[state]}</small></Text>
            ))}
          </Space>
        </>}
      </Card>

      {/* Amounts */}
      <div className="summary-cards">
        <Card size="small" className="summary-card">
          <Text type="secondary"><small>Monthly Installment</small></Text>
          <div><Text strong style={{ fontSize: 18 }}>{convertCentsIntoDollar(loanDetails?.type?.monthlyPayment || 0)}</Text></div>
        </Card>
        <Card size="small" className="summary-card">
          <Text type="secondary"><small>Loan Cost</small></Text>
          <div><Text strong style={{ fontSize: 18 }}>{convertCentsIntoDollar(loanDetails?.type?.cost || 0)}</Text></div>
          {loanDetails?.type?.cost ? <Text type="secondary"><small>{convertCentsIntoDollar(loanDetails.type.cost / loanDetails.type.term)} per month</small></Text> : null}
        </Card>
        <Card size="small" className="summary-card">
          <Text type="secondary"><small>Wallet ({data?.sbLoanEntry?.fundsPlatform || "SILA"})</small></Text>
          <div><Text strong style={{ fontSize: 18 }}>{convertCentsIntoDollar(data?.savingBuilderWalletTotal || 0)}</Text></div>
        </Card>
        <Card size="small" className="summary-card">
          <Text type="secondary"><small>Total Refunded</small></Text>
          <div><Text strong style={{ fontSize: 18 }}>{convertCentsIntoDollar(data?.totalRefunded || 0)}</Text></div>
        </Card>
        <Card size="small" className="summary-card">
          <Text type="secondary"><small>Min Balance for Autopay</small></Text>
          <div><Text strong style={{ fontSize: 18 }}>{convertCentsIntoDollar(autopayMinBalance)}</Text></div>
          <Text type="secondary"><small>Manual: {convertCentsIntoDollar(manualPaymentMinBalance)}</small></Text>
        </Card>
        <Card size="small" className="summary-card">
          <Text type="secondary"><small>Outstanding Payment</small></Text>
          {isPlanDelinquent ? <>
            {/* Delinquent: the overdue amount in red with a red Delinquent tag, and the earliest missed due date */}
            <div>
              <Space size={6} wrap>
                {overdueCents > 0 && <Text strong style={{ color: '#cf1322', fontSize: 18 }}>{convertCentsIntoDollar(overdueCents)}</Text>}
                <Tag color="error" style={{ fontWeight: 600, margin: 0 }}>Delinquent</Tag>
              </Space>
            </div>
            {oldestOverdueDate && <div style={{ marginTop: 6 }}><Tag color="warning" style={{ margin: 0 }}>Due {formatDate(oldestOverdueDate)}</Tag></div>}
            {(overdueCount > 0 || data?.outstandingPaymentStatus) &&
              <Text type="secondary"><small>
                {[overdueCount > 0 && `${overdueCount} missed`, data?.outstandingPaymentStatus && `last attempt ${data.outstandingPaymentStatus}`].filter(Boolean).join(' · ')}
              </small></Text>}
          </> : <div><Text strong style={{ fontSize: 15 }}>{data?.outstandingPaymentStatus || 'N/A'}</Text></div>}
        </Card>
      </div>

      {/* Sections */}
      <PersistedCollapse name={`loan-groups:${data.sbLoanEntry.id}`} className="summary-groups">
        <Panel header={<>Payment Amortization <CountTag count={amortizations.length} /></>} key="schedule">
          <PaymentSchedule paymentSchedule={loanDetails.amortizations} />
        </Panel>
        <Panel
          header={<>Statements <CountTag count={loanStatements?.length ?? 0} /></>}
          key="statements"
          extra={downloadStatement}>
          <StatementList statements={loanStatements} />
        </Panel>
        <Panel
          header={<>Payment History <CountTag count={payments.length} /> {overdueCount > 0 && <CountTag count={overdueCount} label="overdue" problem />}</>}
          key="history">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio.Group
              onChange={(e) => setPaymentType(e.target.value)}
              value={paymentType}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio value="payments">Payments</Radio>
              <Radio value="attempts">Attempts</Radio>
            </Radio.Group>

              {
                paymentType === 'attempts'
                  ? <PaymentAttemptsList paymentAttempts={loanDetails.paymentAttempts} />
                  : <SBPaymentHistoryComponent
                payments={data.allPayments}
              />}
            </Space>
        </Panel>
        <Panel header="Failed Payment Attempts" key="failed">
          <PaymentAttemptsComponent
            userId={userSummary.userId}
            accountId={loanDetails.id}
            paymentAttempts={paymentAttempts}
          />
        </Panel>
      </PersistedCollapse>
    </div>
    );
}
